// src/pages/onboarding/shared/steps/StepDob.jsx
import React, { useEffect, useState } from "react";
import OnboardingTopBar from "@/components/onboarding/OnboardingTopBar";
import OnboardingSection from "@/components/onboarding/OnboardingSection";
import Button from "@/components/ui/Button";
import DatePicker from "@/sot/primitives/datePicker.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import supabase from "@/services/supabaseClient";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { canUseOnboardingDraftMode } from "@/lib/onboardingDraftMode";
import { format } from "date-fns";
import { loadPatientIntroPreview, savePatientIntroPreview } from "@/lib/patientIntroPreview";

function isoToDate(iso) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || "")) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return isNaN(date) ? null : date;
}

function dateToIso(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function isFutureIsoDate(iso = "") {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || "")) return false;
  return iso > dateToIso(new Date());
}

// Small timeout helper
function withTimeout(promise, ms, label = "Request") {
  return new Promise((resolve, reject) => {
    const t = window.setTimeout(() => {
      reject(new Error(`${label} timed out`));
    }, ms);
    promise
      .then((v) => {
        window.clearTimeout(t);
        resolve(v);
      })
      .catch((e) => {
        window.clearTimeout(t);
        reject(e);
      });
  });
}

async function requireUserId() {
  const { data, error } = await withTimeout(
    supabase.auth.getUser(),
    3500,
    "Session"
  );
  const uid = data?.user?.id || null;
  if ((error || !uid) && canUseOnboardingDraftMode()) return "";
  if (error || !uid)
    throw new Error("Your session isn’t ready yet. Please try again.");
  return uid;
}

// Optional local preview (does not replace DB)
function saveDobPreview(profileTable, iso, userId = "") {
  try {
    const key = `afrimed_onb_preview_v1:${profileTable || "patient_profiles"}`;
    const current = JSON.parse(localStorage.getItem(key) || "{}");
    localStorage.setItem(key, JSON.stringify({ ...current, date_of_birth: iso }));
  } catch {
    // ignore
  }

  if (profileTable === "patient_profiles") {
    try {
      const current = loadPatientIntroPreview(userId) || {};
      savePatientIntroPreview({
        ...current,
        dobIso: iso,
      }, userId);
    } catch {
      // ignore
    }
  }
}

export default function StepDob({
  step,
  total,
  value, // ISO "YYYY-MM-DD" (optional, controlled)
  onChange, // setter receiving ISO
  onBack,
  onContinue,

  // ✅ shared: tell it where to upsert
  profileTable = "patient_profiles", // or "doctor_profiles"
  contextNote = "",
  onTelemetry,
}) {
  // 1. Handshake Hook
  const { refreshProfile, user } = useAuth();
  const { t } = useLanguage();

  const isControlled = typeof value === "string" && typeof onChange === "function";

  // locals to keep CTA responsive even if parent is slow
  const [isoLocal, setIsoLocal] = useState(value || "");
  const [textLocal, setTextLocal] = useState("");
  const [showDobPicker, setShowDobPicker] = useState(false);
  useEffect(() => {
    if (!isControlled) return;
    const nextIso = value || "";
    setIsoLocal(nextIso);
    setTextLocal(nextIso ? format(isoToDate(nextIso), "dd-MM-yyyy") : "");
  }, [isControlled, value]);

  const iso = isControlled ? (value || "") : isoLocal;
  const hasValidFormat = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  const futureDob = isFutureIsoDate(iso);
  const valid = hasValidFormat && !futureDob;
  const pickerValue = isoToDate(iso);

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const syncDob = (nextIso, nextText = "") => {
    if (isControlled) onChange?.(nextIso);
    else {
      setIsoLocal(nextIso);
      setTextLocal(nextText);
    }
    setErr("");
    if (nextIso) saveDobPreview(profileTable, nextIso, user?.id || "");
  };
  const displayText = isControlled
    ? (valid ? format(pickerValue, "dd-MM-yyyy") : "")
    : textLocal;

  const handleDateChange = (nextDate) => {
    if (!(nextDate instanceof Date) || Number.isNaN(nextDate.getTime())) {
      if (isControlled) onChange?.("");
      else {
        setIsoLocal("");
        setTextLocal("");
      }
      setErr("");
      return;
    }

    const nextIso = dateToIso(nextDate);
    onTelemetry?.("field_changed", { field: "date_of_birth", value_recorded: false });
    if (isFutureIsoDate(nextIso)) {
      syncDob(nextIso, format(nextDate, "dd-MM-yyyy"));
      setErr(t("onboarding.dob.futureError", "Date of birth cannot be in the future."));
      setShowDobPicker(false);
      return;
    }
    syncDob(nextIso, format(nextDate, "dd-MM-yyyy"));
    setShowDobPicker(false);
  };

async function commitDobToDb(nextIso) {
    const uid = await requireUserId();
    if (!uid) return;

    const payload = {
      user_id: uid,
      date_of_birth: nextIso,
    };

    const { data, error } = await supabase
      .from(profileTable)
      .upsert(payload, { onConflict: "user_id" })
      .select("id")
      .maybeSingle();

    if (error) throw error;
    
    // 2. THE HANDSHAKE: Update global profile state before returning
    if (typeof refreshProfile === "function") {
      await refreshProfile();
    }
    
    if (!data?.id) throw new Error("Could not save your date of birth. Please try again.");
  }

  async function commitAndContinue() {
    if (saving) return;
    setErr("");
    onTelemetry?.("continue_clicked", { valid, saving });

    const nextIso = String(iso || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(nextIso)) {
      onTelemetry?.("validation_blocked", { reason: "invalid_dob" });
      return;
    }
    if (isFutureIsoDate(nextIso)) {
      onTelemetry?.("validation_blocked", { reason: "future_dob" });
      setErr(t("onboarding.dob.futureError", "Date of birth cannot be in the future."));
      return;
    }

    try {
      setSaving(true);
      onTelemetry?.("save_started");

      // Hard-block: DB must save + Handshake must complete
      await commitDobToDb(nextIso);
      onTelemetry?.("save_succeeded");

      onContinue?.();
    } catch (e) {
      onTelemetry?.("save_failed", { reason: "dob_save_failed" });
      const msg = String(
        e?.message || t("onboarding.dob.saveError", "Could not save. Please try again.")
      );
      setErr(
        msg.toLowerCase().includes("timed out")
          ? t(
              "onboarding.dob.timeout",
              "Network timeout while saving. Please try again (or switch network)."
            )
          : msg
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-dvh flex flex-col">
      <OnboardingTopBar step={step} total={total} onBack={onBack} />

      <OnboardingSection
        header={
          <div className="space-y-2">
            <h2 className="text-title-large text-on-surface">
              {t("onboarding.dob.title", "Date of birth")}
            </h2>
            <p className="text-body-medium text-on-surface-var">
              {t(
                "onboarding.dob.subtitle",
                "Make sure this matches your date of birth on your government issued ID."
              )}
            </p>
            {contextNote ? (
              <p className="text-body-small text-on-surface-var">{contextNote}</p>
            ) : null}
          </div>
        }
        footer={
          <Button size="lg" disabled={!valid || saving} onClick={commitAndContinue}>
            {saving
              ? t("onboarding.dob.saving", "Saving...")
              : t("onboarding.dob.continue", "Continue")}
          </Button>
        }
      >
        <div className="space-y-3">
          <div
            className="relative cursor-pointer"
            onClick={() => setShowDobPicker((open) => !open)}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setShowDobPicker((open) => !open);
              }
            }}
          >
            <TextField56
              id="onboarding-dob-display"
              label={t("onboarding.dob.label", "Date of birth")}
              value={displayText}
              onChange={() => {}}
              placeholder={t("onboarding.dob.placeholder", "DD-MM-YYYY")}
              readOnly
              tone="surface"
            />
          {valid && (
            <img
              src="/icons/check-done.svg"
              alt={t("onboarding.dob.done", "Done")}
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-[18px] h-[18px]"
            />
          )}
          </div>
          {showDobPicker && (
            <div className="w-full max-w-[360px]">
              <DatePicker
                value={pickerValue || new Date()}
                onChange={handleDateChange}
                onCancel={() => setShowDobPicker(false)}
                mode="single"
              />
            </div>
          )}
        </div>
        {!!err && (
          <p className="text-[rgb(var(--afrimed-danger))] text-body-medium">
            {err}
          </p>
        )}
      </OnboardingSection>
    </div>
  );
}
